import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { MdEdit, MdCheck, MdClose, MdLock, MdPerson, MdPhone, MdEmail } from "react-icons/md";
import "../styles/Settings.css";

const Settings = ({ user }) => {
  // Estados para os valores dos inputs
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: ""
  });
  
  // Estado para controlar o valor original (para cancelar edição)
  const [originalData, setOriginalData] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [editingField, setEditingField] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          const initialData = {
            name: data.name || "",
            phone: data.phone || "",
            email: data.email || user.email || ""
          };
          setFormData(initialData);
          setOriginalData(initialData);
        }
      } catch (error) {
        console.error("❌ Erro ao buscar dados:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  // Função para iniciar a edição
  const startEditing = (field) => {
    setEditingField(field);
    setMessage({ type: "", text: "" });
  };

  // Função para cancelar a edição (Reverte valor)
  const cancelEditing = () => {
    setFormData(originalData); // Volta ao valor original
    setEditingField(null);
  };

  const handleSave = async () => {
    if (!user?.uid) return;

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const userRef = doc(db, "users", user.uid);
      
      // Atualiza apenas o campo que mudou (ou todos se necessário)
      await updateDoc(userRef, {
        name: formData.name,
        phone: formData.phone,
        updatedAt: serverTimestamp(),
      });

      // Atualiza o estado original com o novo valor salvo
      setOriginalData(formData);
      
      setMessage({ type: "success", text: "Alterações salvas com sucesso!" });
      setEditingField(null);

      // Limpa mensagem após 3 segundos
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);

    } catch (error) {
      console.error("❌ Erro ao salvar:", error);
      setMessage({ type: "error", text: "Erro ao salvar. Tente novamente." });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) return <div className="loading-container"><p>Carregando configurações...</p></div>;

  return (
    <div className="settings-page">
      <div className="settings-container">
        <header className="settings-header">
          <h2>Configurações da Conta</h2>
          <p>Gerencie suas informações pessoais e de contato.</p>
        </header>

        {/* Mensagem de Feedback (Toast) */}
        {message.text && (
          <div className={`settings-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="settings-card">
          
          {/* CAMPO: NOME */}
          <div className={`settings-field ${editingField === "name" ? "active" : ""}`}>
            <div className="field-icon">
              <MdPerson />
            </div>
            <div className="field-content">
              <label>Nome Completo</label>
              {editingField === "name" ? (
                <div className="input-group">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Seu nome"
                    autoFocus
                  />
                  <div className="action-buttons">
                    <button className="save-btn" onClick={handleSave} disabled={saving} title="Salvar">
                      <MdCheck />
                    </button>
                    <button className="cancel-btn" onClick={cancelEditing} disabled={saving} title="Cancelar">
                      <MdClose />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="display-group">
                  <span className="value-text">{formData.name || "Não informado"}</span>
                  <button className="edit-icon-btn" onClick={() => startEditing("name")} title="Editar">
                    <MdEdit />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* CAMPO: TELEFONE */}
          <div className={`settings-field ${editingField === "phone" ? "active" : ""}`}>
            <div className="field-icon">
              <MdPhone />
            </div>
            <div className="field-content">
              <label>Telefone / Celular</label>
              {editingField === "phone" ? (
                <div className="input-group">
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="(00) 00000-0000"
                    autoFocus
                  />
                  <div className="action-buttons">
                    <button className="save-btn" onClick={handleSave} disabled={saving}>
                      <MdCheck />
                    </button>
                    <button className="cancel-btn" onClick={cancelEditing} disabled={saving}>
                      <MdClose />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="display-group">
                  <span className="value-text">{formData.phone || "Não informado"}</span>
                  <button className="edit-icon-btn" onClick={() => startEditing("phone")} title="Editar">
                    <MdEdit />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* CAMPO: EMAIL (READ ONLY) */}
          <div className="settings-field disabled">
            <div className="field-icon">
              <MdEmail />
            </div>
            <div className="field-content">
              <label>Endereço de Email</label>
              <div className="display-group">
                <span className="value-text email-text">{formData.email}</span>
                <div className="locked-badge" title="O email não pode ser alterado">
                  <MdLock />
                </div>
              </div>
              <small className="field-hint">O email de login não pode ser alterado aqui.</small>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Settings;